import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import cn from '@/utils/cn';
import styles from './PlanningDrawer.module.scss';
import { PlanningHeader } from './PlanningHeader';
import { TabbedContent } from './TabbedContent';
import { BUTTON_CLASSES, PlanningContext, Resource, TRANSLATIONS, usePlanningDrawer } from './utils';

interface PlanningDrawerProps {
  planningContext: PlanningContext;
  printPdfPath: string;
  resources: Resource[];
}

interface TriggerPosition {
  height: number;
  left: number;
  top: number;
  width: number;
}

export const PlanningButton = ({ planningContext, printPdfPath, resources }: PlanningDrawerProps) => {
  const { close, isExpanded, isOpen, open } = usePlanningDrawer();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerPos, setTriggerPos] = useState<TriggerPosition | null>(null);

  const updateTriggerPos = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTriggerPos({ height: rect.height, left: rect.left, top: rect.top, width: rect.width });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateTriggerPos();
      window.addEventListener('resize', updateTriggerPos);
      const timer = setTimeout(updateTriggerPos, 50);
      return () => {
        window.removeEventListener('resize', updateTriggerPos);
        clearTimeout(timer);
      };
    }
    setTriggerPos(null);
    return undefined;
  }, [isOpen, updateTriggerPos]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(updateTriggerPos, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isExpanded, isOpen, updateTriggerPos]);

  if (resources.length === 0) return null;

  return (
    <PopoverPrimitive.Root
      modal
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (nextOpen) open();
        else close();
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          ref={triggerRef}
          className={BUTTON_CLASSES}
          type="button"
        >
          {TRANSLATIONS.title(planningContext)}
          <span className="fa fa-chevron-down" />
        </button>
      </PopoverPrimitive.Trigger>
      {isOpen && ReactDOM.createPortal(
        <>
          <div className="tw-fixed tw-z-[1100] tw-inset-0 tw-bg-black tw-bg-opacity-75" />
          {triggerPos && (
            <button
              aria-hidden="true"
              className={cn(BUTTON_CLASSES, 'tw-fixed tw-z-[1150] tw-shadow-none tw-transition-none')}
              style={{ height: triggerPos.height, left: triggerPos.left, top: triggerPos.top, width: triggerPos.width }}
              tabIndex={-1}
              type="button"
              onClick={close}
            >
              {TRANSLATIONS.title(planningContext)}
              <span className="fa fa-close" />
            </button>
          )}
        </>,
        document.body,
      )}
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="end"
          aria-label={TRANSLATIONS.dialog.standard(planningContext)}
          aria-modal="true"
          className={cn(
            styles.popover,
            'tw-z-[1200] tw-flex tw-flex-col tw-bg-white tw-rounded-lg',
            'tw-border-t-[#F16E00] tw-border-t-8 tw-border-solid',
            'tw-w-[50vw] tw-h-[85vh]',
            { [styles.expanded]: isExpanded },
          )}
          side="bottom"
          sideOffset={7}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            const content = e.currentTarget as HTMLElement;
            content.querySelector<HTMLButtonElement>('[role="tab"]')?.focus();
          }}
        >
          <PlanningHeader planningContext={planningContext} printPdfPath={printPdfPath} />
          <TabbedContent resources={resources} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
